import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getEnabledChannels } from '@/lib/queries/channels'
import { getProductsByChannel, getProductCountsByChannel, getProductById } from '@/lib/queries/products'
import { createListing } from '@/lib/queries/listings'

export default function CreateListing() {
  const navigate = useNavigate()
  const location = useLocation()
  const [step, setStep] = useState(1) // 1..5
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [overrideTitle, setOverrideTitle] = useState<boolean>(false)
  const [overrideDescription, setOverrideDescription] = useState<boolean>(false)
  const [overridePrice, setOverridePrice] = useState<boolean>(false)
  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('') // simple text for now
  const [price, setPrice] = useState<string>('') // string for input handling
  const [statusUi, setStatusUi] = useState<'draft' | 'active'>('draft')

  // Load channels
  const { data: channels = [] } = useQuery({
    queryKey: ['channels', 'enabled'],
    queryFn: getEnabledChannels,
  })

  // Load products for selected channel
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['productsByChannel', selectedChannelId],
    queryFn: () => getProductsByChannel(selectedChannelId!),
    enabled: !!selectedChannelId,
    staleTime: 0,
    gcTime: 0,
  })

  // Load product counts per channel for Step 1 display with a single query
  const { data: channelCounts } = useQuery({
    queryKey: ['productCountsByChannel'],
    queryFn: getProductCountsByChannel,
    staleTime: 0,
    gcTime: 0,
  })

  const getCountForChannel = (id: string): number => {
    const match = (channelCounts || []).find((c: any) => c._id === id)
    return match?.count ?? 0
  }

  const selectedProduct = useMemo(
    () => products.find((p: any) => p._id === selectedProductId),
    [products, selectedProductId]
  )

  const productTitle = (selectedProduct as any)?.title || 'Untitled Product'
  const productDescription = '' // list item shape doesn't include description
  const initialPrice = (selectedProduct as any)?.rrp ?? 0

  // Derived listing values
  const effectiveTitle = overrideTitle && title.trim().length > 0 ? title.trim() : productTitle
  const effectiveDescription =
    overrideDescription && description.trim().length > 0 ? description.trim() : String(productDescription || '')
  const effectivePrice = overridePrice && price ? parseFloat(price) : Number(initialPrice || 0)
  // Status is derived at save-time directly from statusUi

  // Create listing mutation
  const { mutateAsync: doCreate, isPending: isSaving } = useMutation({
    mutationFn: createListing,
  })

  // Prefill via URL params: productId and optional step
  const searchParams = new URLSearchParams(location.search)
  const prefillProductId = searchParams.get('productId')
  const prefillStep = Number(searchParams.get('step') || '')

  // Load product for prefill if provided
  const { data: prefillProduct } = useQuery({
    queryKey: ['product', prefillProductId],
    queryFn: () => getProductById(prefillProductId!),
    enabled: !!prefillProductId,
  })

  // If only step is provided (no product), honor it
  useEffect(() => {
    if (!prefillProductId && Number.isFinite(prefillStep) && prefillStep >= 1 && prefillStep <= 5) {
      setStep(prefillStep)
    }
  }, [prefillStep, prefillProductId])

  // Once channels and prefill product are available, set channel and product and advance to step 3
  useEffect(() => {
    if (!prefillProductId || !prefillProduct || !channels?.length) return
    // Choose first mapping with configSet present
    const mappings: Array<any> = Array.isArray(prefillProduct.channelMapping) ? prefillProduct.channelMapping : []
    const mappingWithConfig = mappings.find((m) => m?.configSet && m.configSet._ref && m.channel)
    if (!mappingWithConfig) return
    // Find channel by integrationIdentifier or lowercased name
    const ch = (channels as any[]).find(
      (c) =>
        c?.integrationIdentifier === mappingWithConfig.channel ||
        (c?.name || '').toString().toLowerCase() === String(mappingWithConfig.channel).toLowerCase()
    )
    if (!ch?._id) return
    setSelectedChannelId(ch._id)
    setSelectedProductId(prefillProduct._id)
    if (prefillStep === 3 || !Number.isNaN(prefillStep)) {
      setStep(3)
    } else {
      setStep(3)
    }
  }, [prefillProductId, prefillProduct, channels, prefillStep])

  // Accessible, slidable toggle switch
  const ToggleSwitch = ({
    id,
    checked,
    onChange,
    ariaLabel,
  }: {
    id: string
    checked: boolean
    onChange: (next: boolean) => void
    ariaLabel: string
  }) => {
    const startXRef = useRef<number | null>(null)
    const draggingRef = useRef(false)

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      startXRef.current = e.clientX
      draggingRef.current = true
      ;(e.target as Element).setPointerCapture?.((e as any).pointerId)
    }
    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
      if (startXRef.current == null) return
      const dx = e.clientX - startXRef.current
      draggingRef.current = false
      startXRef.current = null
      const threshold = 10
      if (dx > threshold) onChange(true)
      else if (dx < -threshold) onChange(false)
      else onChange(!checked) // tap
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault()
        onChange(!checked)
      } else if (e.key === 'ArrowRight') {
        onChange(true)
      } else if (e.key === 'ArrowLeft') {
        onChange(false)
      }
    }

    return (
      <div className="relative select-none" style={{ touchAction: 'pan-x' }}>
        <div
          id={id}
          role="switch"
          aria-checked={checked}
          aria-label={ariaLabel}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onKeyDown={onKeyDown}
          className={`w-12 h-7 rounded-full transition-colors ${
            checked ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        />
        <div
          className={`absolute top-0.5 left-0.5 h-6 w-6 bg-white rounded-full transition-transform ${
            checked ? 'translate-x-5' : ''
          }`}
        />
      </div>
    )
  }

  const canNextFromStep1 = !!selectedChannelId
  const canNextFromStep3 = true // optional overrides; always can continue
  const canNextFromStep4 = true // preview only

  const handleSaveDraft = async () => {
    if (!selectedChannelId || !selectedProduct) return
    const selectedChannelObj = (channels || []).find((c: any) => c._id === selectedChannelId)
    const selectedChannelCode: string =
      (selectedChannelObj && (selectedChannelObj.code as string)) ||
      (selectedChannelObj?.name || '').toString().toLowerCase() ||
      'unknown'
    const result = await doCreate({
      productId: selectedProduct._id,
      channel: selectedChannelCode,
      title: effectiveTitle,
      price: effectivePrice,
      description: [], // keep simple for now (portable text could be mapped later)
      status: 'not_synced',
    })
    // Navigate to detail or list
    if (result?._id) {
      navigate(`/listings/${result._id}`)
    } else {
      navigate('/listings')
    }
  }

  const handlePublish = async () => {
    if (!selectedChannelId || !selectedProduct) return
    const selectedChannelObj = (channels || []).find((c: any) => c._id === selectedChannelId)
    const selectedChannelCode: string =
      (selectedChannelObj && (selectedChannelObj.code as string)) ||
      (selectedChannelObj?.name || '').toString().toLowerCase() ||
      'unknown'
    const result = await doCreate({
      productId: selectedProduct._id,
      channel: selectedChannelCode,
      title: effectiveTitle,
      price: effectivePrice,
      description: [],
      status: 'synced',
    })
    if (result?._id) {
      navigate(`/listings/${result._id}`)
    } else {
      navigate('/listings')
    }
  }

  // UI helpers
  const StepHeader = () => (
    <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
      <div className="p-4">
        <h1 className="text-xl font-bold text-gray-900">Create Listing</h1>
        <div className="mt-3 h-1 bg-gray-200 rounded">
          <div
            className="h-1 bg-blue-600 rounded"
            style={{ width: `${(step / 5) * 100}%` }}
          />
        </div>
        {/* Numbered step indicator */}
        <div className="mt-3 flex justify-between px-1">
          {[1, 2, 3, 4, 5].map((n) => {
            const isActive = n === step
            const isCompleted = n < step
            return (
              <div
                key={n}
                aria-label={`Step ${n}`}
                aria-current={isActive ? 'step' : undefined}
                data-state={isActive ? 'active' : isCompleted ? 'completed' : 'inactive'}
                className={[
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
                  isActive ? 'bg-blue-600 text-white' : isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700',
                ].join(' ')}
              >
                {n}
              </div>
            )
          })}
        </div>
      </div>
    </header>
  )

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <StepHeader />

      <main className="p-4">
        {step === 1 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 1: Select Channel</h2>
            <div className="space-y-3">
              {channels.map((ch: any) => {
                const selected = selectedChannelId === ch._id
                const count = getCountForChannel(ch._id)
                return (
                  <button
                    key={ch._id}
                    onClick={() => setSelectedChannelId(ch._id)}
                    className={`w-full text-left border rounded-lg p-3 ${
                      selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'
                    }`}
                    aria-pressed={selected}
                  >
                    <div className="font-medium">{ch.name}</div>
                    <div className="text-xs text-gray-600">{count} products available</div>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {step === 2 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 2: Select Product</h2>
            {productsLoading ? (
              <div className="text-gray-500">Loading products...</div>
            ) : (
              <div className="space-y-3">
                {products.map((p: any) => {
                  const selected = selectedProductId === p._id
                  const selectedChannelObj = (channels || []).find((c: any) => c._id === selectedChannelId)
                  const selectedChannelCode: string =
                    (selectedChannelObj && (selectedChannelObj.code as string)) ||
                    (selectedChannelObj?.name || '').toString().toLowerCase()
                  const hasConfigSet =
                    Array.isArray(p.channelMapping) &&
                    p.channelMapping.some(
                      (m: any) => m?.channel === selectedChannelCode && m?.configSet && m.configSet._ref
                    )
                  return (
                    <button
                      key={p._id}
                      onClick={() => {
                        if (hasConfigSet) setSelectedProductId(p._id)
                      }}
                      disabled={!hasConfigSet}
                      className={`w-full text-left border rounded-lg p-3 ${
                        selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'
                      } ${!hasConfigSet ? 'opacity-50 cursor-not-allowed' : ''}`}
                      aria-pressed={selected}
                      aria-label={p.title}
                    >
                      <div className="font-medium">{p.title || p.name || 'Untitled Product'}</div>
                      <div className="text-xs text-gray-600">
                        {/* Channel derived via domain; show type and base price */}
                        {p.isPrintOnDemand ? 'POD' : 'Physical'} • £
                        {(p.rrp ?? p.basePrice ?? 0).toFixed(2)}
                      </div>
                      {!hasConfigSet && (
                        <div className="text-[11px] text-red-600 mt-1">
                          Missing channel config. Add a config set to select this product.
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
            {/* Inline actions like wireframe - always visible */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                className="flex-1 bg-gray-100 text-gray-900 py-3 px-4 rounded-lg font-medium touch-target-min-44"
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <button
                type="button"
                data-testid="inline-next-step2"
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
                onClick={() => setStep(3)}
                disabled={!selectedProductId}
              >
                Next
              </button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 3: Configure Listing</h2>
            <div className="bg-blue-50 rounded-md p-3 text-sm text-gray-800 mb-3">
              <div>
                <strong>Product:</strong> {productTitle}
              </div>
              <div>
                <strong>Base Price:</strong> £{Number(initialPrice).toFixed(2)}
              </div>
            </div>

            {/* Title override */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="toggle-title" className="block text-sm font-medium text-gray-700 m-0">
                  Override Title
                </label>
                <ToggleSwitch
                  id="toggle-title"
                  ariaLabel="Override Title"
                  checked={overrideTitle}
                  onChange={setOverrideTitle}
                />
              </div>
              <input
                type="text"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
                placeholder={productTitle}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={!overrideTitle}
              />
            </div>

            {/* Description override */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="toggle-description" className="block text-sm font-medium text-gray-700 m-0">
                  Override Description
                </label>
                <ToggleSwitch
                  id="toggle-description"
                  ariaLabel="Override Description"
                  checked={overrideDescription}
                  onChange={setOverrideDescription}
                />
              </div>
              <textarea
                rows={4}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base"
                placeholder={String(productDescription || '')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!overrideDescription}
              />
            </div>

            {/* Price override */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 m-0" htmlFor="price">
                  Override Price
                </label>
                <ToggleSwitch
                  id="toggle-price"
                  ariaLabel="Override Price"
                  checked={overridePrice}
                  onChange={setOverridePrice}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">£</span>
                <input
                  id="price"
                  aria-label="Price"
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder={String(initialPrice ?? '')}
                  disabled={!overridePrice}
                />
              </div>
            </div>

            {/* Status */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
                value={statusUi}
                onChange={(e) => setStatusUi(e.target.value as 'draft' | 'active')}
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
              </select>
            </div>
          </section>
        )}

        {step === 4 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 4: Preview</h2>
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="w-full aspect-square bg-gray-100 rounded-lg mb-3" />
              <div className="text-lg font-semibold mb-1">{effectiveTitle}</div>
              <div className="text-xs text-gray-600 mb-2">
                Channel:{' '}
                {(() => {
                  const ch = (channels || []).find((c: any) => c._id === selectedChannelId)
                  return ch?.name || 'Unknown'
                })()}{' '}
                • {(selectedProduct as any)?.isPrintOnDemand ? 'POD' : 'Physical'}
              </div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap mb-2">
                {effectiveDescription || 'No description'}
              </div>
              <div className="text-2xl font-bold text-blue-600">£{Number(effectivePrice).toFixed(2)}</div>
              {overridePrice && (
                <div className="text-xs text-green-600 mt-1">
                  ✓ Price overridden from £{Number(initialPrice).toFixed(2)}
                </div>
              )}
            </div>
            {/* Inline actions like wireframe */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                className="flex-1 bg-gray-100 text-gray-900 py-3 px-4 rounded-lg font-medium touch-target-min-44"
                onClick={() => setStep(3)}
              >
                Back
              </button>
              <button
                type="button"
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44"
                onClick={() => setStep(5)}
              >
                Next
              </button>
            </div>
          </section>
        )}

        {step === 5 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 5: Publish or Save</h2>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4 text-sm text-gray-800">
              <div>
                <strong>Channel:</strong>{' '}
                {(() => {
                  const ch = (channels || []).find((c: any) => c._id === selectedChannelId)
                  return ch?.name || 'Unknown'
                })()}
              </div>
              <div>
                <strong>Product:</strong> {productTitle}
              </div>
              <div>
                <strong>Title:</strong> {effectiveTitle}
              </div>
              <div>
                <strong>Price:</strong> £{Number(effectivePrice).toFixed(2)}{' '}
                {overridePrice && <span className="text-green-700">(overridden)</span>}
              </div>
              <div>
                <strong>Status:</strong> {statusUi === 'active' ? 'Active' : 'Draft'}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer actions */}
      <footer className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
        {step > 1 ? (
          <button
            className="flex-1 bg-gray-100 text-gray-900 py-3 px-4 rounded-lg font-medium touch-target-min-44"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
          >
            Back
          </button>
        ) : (
          <button
            className="flex-1 bg-gray-100 text-gray-900 py-3 px-4 rounded-lg font-medium touch-target-min-44"
            onClick={() => navigate('/listings')}
          >
            Cancel
          </button>
        )}

        {step < 5 ? (
          <button
            className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
            onClick={() => setStep((s) => s + 1)}
            disabled={
              (step === 1 && !canNextFromStep1) ||
              (step === 3 && !canNextFromStep3) ||
              (step === 4 && !canNextFromStep4)
            }
          >
            Next
          </button>
        ) : (
          <>
            <button
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
              onClick={handlePublish}
              disabled={isSaving || !selectedProductId || !selectedChannelId}
            >
              Publish to Channel
            </button>
            <button
              className="flex-1 bg-gray-100 text-gray-900 py-3 px-4 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
              onClick={handleSaveDraft}
              disabled={isSaving || !selectedProductId || !selectedChannelId}
            >
              Save as Draft
            </button>
          </>
        )}
      </footer>
    </div>
  )
}

